import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
} from "@nymbl/nos2-rules";
import { type FormEvent, useState } from "react";
import {
  usePeopleByEmail,
  usePersonLink,
  useRemovePersonLink,
  useSetPersonLink,
} from "@/hooks/use-person-links";
import type { User } from "@/hooks/use-users";

interface LinkPersonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: User;
}

/**
 * Links a sign-in to the Nymbl person it belongs to when their emails differ (DECISIONS
 * 2026-10-28). The admin types the person's Nymbl email, then picks the active match.
 */
export function LinkPersonDialog({ open, onOpenChange, user }: LinkPersonDialogProps) {
  const [draft, setDraft] = useState("");
  const [searched, setSearched] = useState("");
  const link = usePersonLink(user.id, open);
  const people = usePeopleByEmail(searched);
  const setLink = useSetPersonLink(user.id);
  const removeLink = useRemovePersonLink(user.id);
  const busy = setLink.isPending || removeLink.isPending;

  function handleFind(event: FormEvent) {
    event.preventDefault();
    setSearched(draft.trim().toLowerCase());
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link Nymbl person</DialogTitle>
          <DialogDescription className="sr-only">
            Link {user.name} ({user.email}) to the Nymbl person they are.
          </DialogDescription>
        </DialogHeader>

        {link.isLoading ? (
          <Skeleton className="h-11 w-full" />
        ) : link.isError ? (
          <p role="alert" className="text-sm text-destructive">
            {link.error.message}
          </p>
        ) : link.data ? (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
            <div className="min-w-0">
              <p className="font-medium">{link.data.personName}</p>
              <p className="truncate text-sm text-muted-foreground">{link.data.personEmail}</p>
            </div>
            <Button
              variant="outline"
              className="min-h-11 text-destructive"
              disabled={busy}
              aria-label={`Remove link from ${user.email} to ${link.data.personName}`}
              onClick={() => removeLink.mutate()}
            >
              Remove link
            </Button>
          </div>
        ) : null}

        <form onSubmit={handleFind} className="space-y-2">
          <Label htmlFor="link-person-email">Nymbl person's email</Label>
          <div className="flex gap-2">
            <Input
              id="link-person-email"
              type="email"
              autoComplete="off"
              placeholder="name@nymbl.app"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              className="min-h-11"
            />
            <Button type="submit" variant="outline" className="min-h-11" disabled={!draft.trim()}>
              Find
            </Button>
          </div>
        </form>

        {searched && (
          <div aria-live="polite" className="space-y-2">
            {people.isLoading ? (
              <Skeleton className="h-14 w-full" />
            ) : people.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {people.error.message}
              </p>
            ) : (people.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No Nymbl person has that email.</p>
            ) : (
              (people.data ?? []).map((person) => (
                <div
                  key={person.personId}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="font-medium">{person.name}</p>
                    <p className="truncate text-sm text-muted-foreground">{person.email}</p>
                  </div>
                  {person.isActive ? (
                    <Button
                      className="min-h-11"
                      disabled={busy || link.data?.personId === person.personId}
                      aria-label={
                        link.data?.personId === person.personId
                          ? `Linked: ${user.email} to ${person.name}`
                          : `Link ${user.email} to ${person.name}`
                      }
                      onClick={() =>
                        setLink.mutate(
                          { personId: person.personId, personEmail: person.email },
                          { onSuccess: () => onOpenChange(false) },
                        )
                      }
                    >
                      {link.data?.personId === person.personId ? "Linked" : "Link"}
                    </Button>
                  ) : (
                    <Badge variant="secondary">Inactive</Badge>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
